// src/components/homeowner-preferences-quiz/index.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ChevronLeft, ChevronRight, InfoOutlined, Close } from "@mui/icons-material";
import { useDashboardFooter } from "../../contexts/DashboardFooterContext";
import './styles.scss';
import {
  ACQUISITION_PREFS,
  BILLING_METHODS,
  DESIGNER_ROLES,
  EXPERIENCE_OPTIONS,
  GENDER_OPTIONS,
  LEAD_READINESS_OPTIONS,
  MINORITY_OPTIONS,
  PARTNERSHIP_STYLES,
  PET_OPTIONS,
  POLITICAL_AFFILIATIONS,
  SKILL_SETS,
  YES_NO_NOPREF,
  PreferencesResult,
} from "./constants";
import { SkillRanker } from "./SkillRanker";

/** Small helper card that matches the 3-column selectable blocks in the mocks */
const SelectableCard: React.FC<{
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ selected, onClick, children }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      padding: "0 20px",
      borderColor: "secondary.main",
      ...(selected && {
        bgcolor: "secondary.main",
        color: "common.white",
        "& .MuiTypography-root": { color: "inherit" },
      }),
    }}
  >
    <CardActionArea onClick={onClick} sx={{ height: "100%" }}>
      <CardContent>
        <Typography fontWeight={600} lineHeight={1.35} color={selected ? "common.white" : "secondary.main"}>
          {children}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

/** Reusable title block */
const SectionTitle: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <Box textAlign="center" mb={4}>
    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
      {title}
    </Typography>
    {subtitle && <Typography color="text.secondary">{subtitle}</Typography>}
  </Box>
);

/** Single-select chips (used in steps 6, 8, 9, 10, 11, 12, 13, 14) */
const SelectOneChips: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  center?: boolean;
}> = ({ options, value, onChange, center }) => (
  <Box
    display="flex"
    flexWrap="wrap"
    gap={1}
    justifyContent={center ? "center" : "flex-start"}
  >
    {options.map((o) => {
      const selected = value === o.value;
      return (
        <Chip
          key={o.value}
          label={o.label}
          onClick={() => onChange(o.value)}
          variant={selected ? "filled" : "outlined"}
          color="secondary"
          sx={{ 
            fontSize: "1rem", 
            py: 0.5, // 8px
            px: 0.5, // 8px
            height: "auto", 
            minWidth: 'auto',
            width: 'fit-content',
            borderRadius: 50 
          }}
        />
      );
    })}
  </Box>
);

export interface HomeownerPreferencesQuizProps {
  /** Figma shows "Continue X of 15" */
  totalSubSteps?: number;
  onComplete?: (result: PreferencesResult) => void;
  onBack?: () => void;
  initialData?: PreferencesResult | null;
}

/**
 * Homeowner Preferences Quiz – implements steps 1–15 per Figma.
 */
export const HomeownerPreferencesQuiz: React.FC<
  HomeownerPreferencesQuizProps
> = ({ totalSubSteps = 15, onComplete, onBack, initialData }) => {
  const { setFooter, clearFooter } = useDashboardFooter();
  const isMobile = useMediaQuery('(max-width:600px)');
  const hasUserInteractedRef = useRef(false);

  const normalizeKey = (value: string) =>
    value
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const normalizeOptionValue = (
    raw: string | null | undefined,
    options: { value: string; label: string }[]
  ) => {
    if (!raw) return "";
    const rawKey = normalizeKey(raw);
    const byValue = options.find((o) => normalizeKey(o.value) === rawKey);
    if (byValue) return byValue.value;
    const byLabel = options.find((o) => normalizeKey(o.label) === rawKey);
    if (byLabel) return byLabel.value;
    const byPartial = options.find((o) => {
      const labelKey = normalizeKey(o.label);
      const valueKey = normalizeKey(o.value);
      return (
        labelKey.includes(rawKey) ||
        rawKey.includes(labelKey) ||
        valueKey.includes(rawKey) ||
        rawKey.includes(valueKey)
      );
    });
    if (byPartial) return byPartial.value;
    return raw;
  };

  const normalizeOptionArray = (
    raw: string[] | null | undefined,
    options: { value: string; label: string }[]
  ) => {
    if (!raw?.length) return [];
    return raw
      .map((value) => normalizeOptionValue(value, options))
      .filter((value) => options.some((o) => o.value === value));
  };

  const normalizeSkillRankings = (raw: string[] | null | undefined) => {
    if (!raw?.length) return [];
    return raw
      .map((value) => {
        const match = SKILL_SETS.find(
          (skill) => {
            const skillKey = normalizeKey(skill);
            const valueKey = normalizeKey(String(value));
            return (
              skillKey === valueKey ||
              skillKey.includes(valueKey) ||
              valueKey.includes(skillKey)
            );
          }
        );
        return match || String(value);
      })
      .filter(Boolean);
  };

  // Priorities step 1 also uses `preferences.hireLikelihood` (legacy schema). We
  // must not hydrate Preferences step 15 from that value.
  const isPriorityHireLikelihoodValue = (value: string | null | undefined) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return false;
    return (
      normalized.includes("absolute") ||
      normalized.includes("highly likely") ||
      normalized.includes("warm opportunity") ||
      normalized.includes("not likely")
    );
  };

  // step machine
  const [step, setStep] = useState(1);
  const lastStep = 15;
  
  // Mobile tooltip state for billing methods
  const [mobileTooltipOpen, setMobileTooltipOpen] = useState<string | null>(null);
  const handleMobileTooltipOpen = (value: string) => {
    setMobileTooltipOpen(value);
  };
  const handleMobileTooltipClose = () => {
    setMobileTooltipOpen(null);
  };

  // state per step - initialize from initialData if provided
  const [budget, setBudget] = useState<string>(initialData?.budgetRange || "");
  const [billing, setBilling] = useState<string[]>(initialData?.billingMethods || []); // step 1
  const [financing, setFinancing] = useState<"YES" | "NO">(
    initialData?.wantsFinancing === "YES" || initialData?.wantsFinancing === "yes" ? "YES" : "NO"
  );
  const [partnerStyle, setPartnerStyle] = useState<string>(initialData?.partnershipStyle || ""); // step 2
  const [designerRole, setDesignerRole] = useState<string>(initialData?.designerRole || ""); // step 3
  const [skills, setSkills] = useState<string[]>(initialData?.skillRankings || []); // step 4
  const [acquisition, setAcquisition] = useState<string>(initialData?.acquisitionPreference || ""); // step 5
  const [genderPref, setGenderPref] = useState<string>(initialData?.designerGenderPreference || ""); // step 6
  const [petPref, setPetPref] = useState<string[]>(initialData?.designerPetPreference || []); // step 7
  const [lgbtq, setLgbtq] = useState<string>(initialData?.lgbtqFriendly || ""); // step 8
  const [womenOwned, setWomenOwned] = useState<string>(initialData?.womenOwnedFirm || ""); // step 9
  const [minorityPref, setMinorityPref] = useState<string>(initialData?.minorityOwnedPreference || ""); // step 10
  const [samePolitics, setSamePolitics] = useState<string>(initialData?.samePolitics || ""); // step 11
  const [political, setPolitical] = useState<string>(initialData?.politicalAffiliation || ""); // step 11
  const [politicalOther, setPoliticalOther] = useState<string>((initialData as any)?.politicalOtherText || ""); // step 11

  useEffect(() => {
    if (!initialData) return;

    if (initialData.budgetRange !== undefined) {
      setBudget(initialData.budgetRange || "");
    }

    if (hasUserInteractedRef.current) return;

    if (initialData.billingMethods !== undefined) {
      setBilling(normalizeOptionArray(initialData.billingMethods, BILLING_METHODS));
    }
    if (initialData.wantsFinancing !== undefined) {
      setFinancing(String(initialData.wantsFinancing).toUpperCase() === "YES" ? "YES" : "NO");
    }
    if (initialData.partnershipStyle !== undefined) {
      setPartnerStyle(normalizeOptionValue(initialData.partnershipStyle || "", PARTNERSHIP_STYLES));
    }
    if (initialData.designerRole !== undefined) {
      setDesignerRole(normalizeOptionValue(initialData.designerRole || "", DESIGNER_ROLES));
    }
    if (initialData.skillRankings !== undefined) {
      setSkills(normalizeSkillRankings(initialData.skillRankings));
    }
    if (initialData.acquisitionPreference !== undefined) {
      setAcquisition(normalizeOptionValue(initialData.acquisitionPreference || "", ACQUISITION_PREFS));
    }
    if (initialData.designerGenderPreference !== undefined) {
      setGenderPref(normalizeOptionValue(initialData.designerGenderPreference || "", GENDER_OPTIONS));
    }
    if (initialData.designerPetPreference !== undefined) {
      setPetPref(normalizeOptionArray(initialData.designerPetPreference, PET_OPTIONS));
    }
    if (initialData.lgbtqFriendly !== undefined) {
      setLgbtq(normalizeOptionValue(initialData.lgbtqFriendly || "", YES_NO_NOPREF));
    }
    if (initialData.womenOwnedFirm !== undefined) {
      setWomenOwned(normalizeOptionValue(initialData.womenOwnedFirm || "", YES_NO_NOPREF));
    }
    if (initialData.minorityOwnedPreference !== undefined) {
      setMinorityPref(normalizeOptionValue(initialData.minorityOwnedPreference || "", MINORITY_OPTIONS));
    }
    if (initialData.samePolitics !== undefined) {
      setSamePolitics(normalizeOptionValue(initialData.samePolitics || "", YES_NO_NOPREF));
    }
    if (initialData.politicalAffiliation !== undefined) {
      setPolitical(normalizeOptionValue(initialData.politicalAffiliation || "", POLITICAL_AFFILIATIONS));
    }
    if (initialData.politicalOtherText !== undefined) {
      setPoliticalOther(initialData.politicalOtherText || "");
    }
    if (initialData.sameAgeRangePreference !== undefined) {
      setSameAgeRange(normalizeOptionValue(initialData.sameAgeRangePreference || "", YES_NO_NOPREF));
    }
    if (initialData.desiredAgeRange !== undefined) {
      setDesiredAgeRange(normalizeOptionValue(initialData.desiredAgeRange || "", AGE_RANGE_OPTIONS));
    }
    if (initialData.hasChildrenPreference !== undefined) {
      setHasChildren(normalizeOptionValue(initialData.hasChildrenPreference || "", YES_NO_NOPREF));
    }
    if (initialData.experiencePreference !== undefined) {
      setExperience(normalizeOptionValue(initialData.experiencePreference || "", EXPERIENCE_OPTIONS));
    }
    const explicitHireReason = (initialData as any).hireReason;
    const hasExplicitHireReason = explicitHireReason !== undefined && explicitHireReason !== null;
    const legacyHireLikelihood = initialData.hireLikelihood;
    const legacyLooksLikePreferenceReason =
      !!legacyHireLikelihood && !isPriorityHireLikelihoodValue(legacyHireLikelihood);

    if (hasExplicitHireReason || legacyHireLikelihood !== undefined) {
      const rawHireReason = hasExplicitHireReason
        ? String(explicitHireReason || "")
        : legacyLooksLikePreferenceReason
          ? String(legacyHireLikelihood || "")
          : "";
      const normalized = normalizeOptionValue(rawHireReason, LEAD_READINESS_OPTIONS);
      const isKnown = LEAD_READINESS_OPTIONS.some((o) => o.value === normalized);
      if (isKnown) {
        setHireReason(normalized);
        setHireReasonOther("");
      } else if (rawHireReason) {
        setHireReason("OTHER");
        setHireReasonOther(String(rawHireReason));
      } else {
        setHireReason("");
        setHireReasonOther("");
      }
    }
  }, [initialData]);

  const isYes = (v: string) => (v || "").toLowerCase().startsWith("yes");
  const handleSamePoliticsChange = (v: string) => {
    hasUserInteractedRef.current = true;
    setSamePolitics(v);
    if (!isYes(v)) {          // only clear when not "Yes"
      setPolitical("");
      setPoliticalOther("");
    }
  };

  // Step 12 (Age range)
  const [sameAgeRange, setSameAgeRange] = useState<string>(initialData?.sameAgeRangePreference || ""); 
  const AGE_RANGE_OPTIONS = [
    { value: "18_24", label: "18-24" },
    { value: "25_34", label: "25-34" },
    { value: "35_44", label: "35-44" },
    { value: "45_54", label: "45-54" },
    { value: "55_64", label: "55-64" },
    { value: "65_PLUS", label: "65 or older" },
  ];
  const [desiredAgeRange, setDesiredAgeRange] = useState<string>(initialData?.desiredAgeRange || "");

  const handleSameAgeChange = (v: string) => {
    hasUserInteractedRef.current = true;
    setSameAgeRange(v);
    if (v !== "YES") setDesiredAgeRange("");
  };

  const [hasChildren, setHasChildren] = useState<string>(initialData?.hasChildrenPreference || ""); // step 13
  const [experience, setExperience] = useState<string>(initialData?.experiencePreference || ""); // step 14
  const [hireReason, setHireReason] = useState<string>(""); // step 15
  const [hireReasonOther, setHireReasonOther] = useState<string>("");

  // validation per step
  const disableNext = useMemo(() => {
    switch (step) {
      case 1:
        return billing.length === 0;
      case 2:
        return !partnerStyle;
      case 3:
        return !designerRole;
      case 4:
        return skills.length === 0;
      case 5:
        return !acquisition;
      case 6:
        return !genderPref;
      case 7:
        return petPref.length === 0;
      case 8:
        return !lgbtq;
      case 9:
        return !womenOwned;
      case 10:
        return !minorityPref;
      case 11: {
        if (!samePolitics) return true;
        if (isYes(samePolitics)) { 
          if (!political) return true;
          if (political.toLowerCase() === "other" && !politicalOther.trim()) return true;
        }
        return false;    
      }        
      case 12: {
        if (!sameAgeRange) return true;                    // must pick Yes/No/No Pref
        if (sameAgeRange === "YES" && !desiredAgeRange) return true; // if Yes, pick a range
        return false;
      }
      case 13:
        return !hasChildren;
      case 14:
        return !experience;
      case 15:
        if (!hireReason) return true;
        if (hireReason === "OTHER" && !hireReasonOther.trim()) return true;
        return false;
      default:
        return false;
    }
  }, [
    step,
    billing,
    partnerStyle,
    designerRole,
    skills,
    acquisition,
    genderPref,
    petPref,
    lgbtq,
    womenOwned,
    minorityPref,
    samePolitics,
    political,
    politicalOther,
    sameAgeRange,
    desiredAgeRange,
    hasChildren,
    experience,
    hireReason,
    hireReasonOther,
  ]);

  const goBack = useCallback(() => {
    if (step > 1) setStep((s) => s - 1);
    else onBack?.();
  }, [step, onBack]);

  const goNext = useCallback(() => {
    if (step < lastStep) {
      setStep((s) => s + 1);
      return;
    }
    // use 'any' to avoid strict coupling to the PreferencesResult shape here
    const finalHireReason =
      hireReason === "OTHER" ? hireReasonOther.trim() : hireReason;
    const payload: any = {
      budgetRange: budget || undefined,
      billingMethods: billing,
      wantsFinancing: financing,
      partnershipStyle: partnerStyle,
      designerRole,
      skillRankings: skills.length ? skills : SKILL_SETS,
      acquisitionPreference: acquisition,
      designerGenderPreference: genderPref,
      designerPetPreference: petPref,
      lgbtqFriendly: lgbtq,
      womenOwnedFirm: womenOwned,
      minorityOwnedPreference: minorityPref,
      samePolitics,
      politicalAffiliation: political,
      politicalOtherText: political === "OTHER" ? politicalOther : undefined,
      // step 12 additions
      sameAgeRangePreference: sameAgeRange,
      desiredAgeRange,
      hasChildrenPreference: hasChildren,
      experiencePreference: experience,
      hireReason: finalHireReason,
    } as PreferencesResult & Record<string, any>;
    onComplete?.(payload);
  }, [
    step,
    budget,
    billing,
    financing,
    partnerStyle,
    designerRole,
    skills,
    acquisition,
    genderPref,
    petPref,
    lgbtq,
    womenOwned,
    minorityPref,
    samePolitics,
    political,
    politicalOther,
    sameAgeRange,
    desiredAgeRange,
    hasChildren,
    experience,
    hireReason,
    hireReasonOther,
    onComplete,
  ]);

  // sticky footer like project-quiz
  useEffect(() => {
    setFooter(
      <>
        <Button 
          onClick={goBack} 
          variant="text" 
          startIcon={<ChevronLeft sx={{ width: 16, height: 16 }} />}
          sx={{ fontSize: { xs: '14px', sm: '16px' } }}
        >
          Back
        </Button>
        <Button
          onClick={goNext}
          variant="contained"
          endIcon={<ChevronRight sx={{ width: 16, height: 16 }} />}
          disabled={disableNext}
          sx={{ fontSize: { xs: '14px', sm: '16px' } }}
        >
          {step === lastStep
            ? "Save And Continue"
            : `Continue ${step} of ${totalSubSteps}`}
        </Button>
      </>
    );
    return () => clearFooter();
  }, [
    goBack,
    goNext,
    disableNext,
    step,
    totalSubSteps,
    setFooter,
    clearFooter,
  ]);

  // util
  const toggle = (arr: string[], set: (v: string[]) => void, val: string) => {
    hasUserInteractedRef.current = true;
    set(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  return (
    <Box pt={0} pb={{ xs: 2, md: 4 }}>
      {/* 1. Billing + Financing (3 columns + Switch) */}
      {step === 1 && (
        <Container maxWidth="lg" sx={{ paddingLeft: { xs: 2, md: 20 }, paddingRight: { xs: 2, md: 20 } }}>
          <SectionTitle
            title="How would you like to be billed for your design project?"
            subtitle="(Select All That Apply)"
          />
          <Grid container spacing={2}>
              {BILLING_METHODS.map((o) => {
                const selected = billing.includes(o.value);
                return (
                <Grid item xs={12} sm={6} md={4} key={o.value}>
                  <Card
                    onClick={() => toggle(billing, setBilling, o.value)}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      minHeight: isMobile ? '44px' : '180px',
                      border: selected 
                        ? '2px solid' 
                        : '1px solid',
                      borderColor: selected 
                        ? 'secondary.main' 
                        : 'divider',
                      backgroundColor: selected
                        ? 'secondary.main'
                        : 'transparent',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: 'secondary.main',
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <CardContent sx={{ 
                      p: isMobile ? 2 : 3, 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'flex-start' 
                    }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={isMobile ? 1 : 1.5}>
                        <Typography 
                          variant={isMobile ? "body1" : "h6"}
                          sx={{ 
                            fontWeight: 'bold',
                            color: selected ? '#FFFFFF' : 'secondary.main',
                            textAlign: 'left',
                            flex: 1,
                            fontSize: isMobile ? '0.9rem' : undefined
                    }}
                  >
                    {o.label}
                        </Typography>
                        {isMobile ? (
                          <>
                            <IconButton
                              size="small"
                              sx={{
                                width: 44,
                                height: 44,
                                color: selected ? '#FFFFFF' : 'secondary.main',
                                '&:hover': {
                                  backgroundColor: selected 
                                    ? 'rgba(255, 255, 255, 0.1)' 
                                    : 'rgba(233, 30, 99, 0.1)'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMobileTooltipOpen(o.value);
                              }}
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                            
                            {/* Mobile Dialog */}
                            <Dialog
                              open={mobileTooltipOpen === o.value}
                              onClose={handleMobileTooltipClose}
                              maxWidth="sm"
                              fullWidth
                            >
                              <DialogTitle>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="h6" fontWeight="bold">
                                    {o.label}
                                  </Typography>
                                  <IconButton
                                    onClick={handleMobileTooltipClose}
                                    size="small"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <Close />
                                  </IconButton>
            </Box>
                              </DialogTitle>
                              <DialogContent>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    whiteSpace: 'pre-line',
                                    lineHeight: 1.6
                                  }}
                                >
                                  {o.helperText}
                                </Typography>
                              </DialogContent>
                            </Dialog>
                          </>
                        ) : (
                          o.helperText && (
                            <Tooltip 
                              title={
                                <Box sx={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
                                  {o.helperText}
          </Box>
                              }
                              placement="top"
                              arrow
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  width: 44,
                                  height: 44,
                                  color: selected ? '#FFFFFF' : 'secondary.main',
                                  '&:hover': {
                                    backgroundColor: selected 
                                      ? 'rgba(255, 255, 255, 0.1)' 
                                      : 'rgba(233, 30, 99, 0.1)'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box
            mt={6}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            maxWidth="lg"
            mx="auto"
          >
            <Typography variant="h6">
              Would you be interested in financing options for your project?
            </Typography>
            <FormControlLabel
              label="Yes/No"
              labelPlacement="start"
              control={
                <Switch
                  checked={financing === "YES"}
                  onChange={(_, checked) => {
                    hasUserInteractedRef.current = true;
                    setFinancing(checked ? "YES" : "NO");
                  }}
                  color="secondary"
                />
              }
              sx={{ mr: 1 }}
            />
          </Box>
        </Container>
      )}

      {/* 2. Partnership Style (3 columns) */}
      {step === 2 && (
        <Container maxWidth="lg">
          <SectionTitle
            title="How do you envision working with your designer?"
            subtitle="Please select your preferred partnership style."
          />
          <Grid container spacing={2}>
            {PARTNERSHIP_STYLES.map((o) => {
              const selected = partnerStyle === o.value;
              return (
                <Grid item xs={12} sm={6} md={4} key={o.value}>
                  <Card
                  onClick={() => {
                    hasUserInteractedRef.current = true;
                    setPartnerStyle(o.value);
                  }}
                  sx={{
                      cursor: 'pointer',
                      height: '100%',
                      minHeight: isMobile ? '44px' : '180px',
                      border: selected 
                        ? '2px solid' 
                        : '1px solid',
                      borderColor: selected 
                        ? 'secondary.main' 
                        : 'divider',
                      backgroundColor: selected
                        ? 'secondary.main'
                        : 'transparent',
                      boxShadow: 'none',
                    '&:hover': {
                        borderColor: 'secondary.main',
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <CardContent sx={{ 
                      p: isMobile ? 2 : 3, 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'flex-start' 
                    }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={isMobile ? 1 : 1.5}>
                        <Typography 
                          variant={isMobile ? "body1" : "h6"}
                          sx={{ 
                            fontWeight: 'bold',
                            color: selected ? '#FFFFFF' : 'secondary.main',
                            textAlign: 'left',
                            flex: 1,
                            fontSize: isMobile ? '0.9rem' : undefined
                          }}
                        >
                          {o.label}
                        </Typography>
                        {isMobile ? (
                          <>
                            <IconButton
                              size="small"
                              sx={{
                                width: 44,
                                height: 44,
                                color: selected ? '#FFFFFF' : 'secondary.main',
                                '&:hover': {
                                  backgroundColor: selected 
                                    ? 'rgba(255, 255, 255, 0.1)' 
                                    : 'rgba(233, 30, 99, 0.1)'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMobileTooltipOpen(o.value);
                              }}
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                            
                            {/* Mobile Dialog */}
                            <Dialog
                              open={mobileTooltipOpen === o.value}
                              onClose={handleMobileTooltipClose}
                              maxWidth="sm"
                              fullWidth
                            >
                              <DialogTitle>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="h6" fontWeight="bold">
                                    {o.label}
                                  </Typography>
                                  <IconButton
                                    onClick={handleMobileTooltipClose}
                                    size="small"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <Close />
                                  </IconButton>
            </Box>
                              </DialogTitle>
                              <DialogContent>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    whiteSpace: 'pre-line',
                                    lineHeight: 1.6
                                  }}
                                >
                                  {o.helperText}
                                </Typography>
                              </DialogContent>
                            </Dialog>
                          </>
                        ) : (
                          o.helperText && (
                            <Tooltip 
                              title={
                                <Box sx={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
                                  {o.helperText}
          </Box>
                              }
                              placement="top"
                              arrow
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  width: 44,
                                  height: 44,
                                  color: selected ? '#FFFFFF' : 'secondary.main',
                                  '&:hover': {
                                    backgroundColor: selected 
                                      ? 'rgba(255, 255, 255, 0.1)' 
                                      : 'rgba(233, 30, 99, 0.1)'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      )}

      {/* 3. Designer Role / Approach (3 columns) */}
      {step === 3 && (
        <Container maxWidth="lg">
          <SectionTitle
            title="Please select your desired level of designer services you are looking for:"
          />
          <Grid container spacing={2}>
            {DESIGNER_ROLES.map((o) => {
              const selected = designerRole === o.value;
              return (
                <Grid item xs={12} sm={6} md={4} key={o.value}>
                  <Card
                  onClick={() => {
                    hasUserInteractedRef.current = true;
                    setDesignerRole(o.value);
                  }}
                  sx={{
                      cursor: 'pointer',
                      height: '100%',
                      minHeight: isMobile ? '44px' : '180px',
                      border: selected 
                        ? '2px solid' 
                        : '1px solid',
                      borderColor: selected 
                        ? 'secondary.main' 
                        : 'divider',
                      backgroundColor: selected
                        ? 'secondary.main'
                        : 'transparent',
                      boxShadow: 'none',
                    '&:hover': {
                        borderColor: 'secondary.main',
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <CardContent sx={{ 
                      p: isMobile ? 2 : 3, 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'flex-start' 
                    }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={isMobile ? 1 : 1.5}>
                        <Box sx={{ flex: 1 }}>
                          {(() => {
                            const colonIndex = o.label.indexOf(':');
                            const title = colonIndex !== -1 ? o.label.substring(0, colonIndex + 1) : o.label;
                            const description = colonIndex !== -1 ? o.label.substring(colonIndex + 1).trim() : '';
                            
                            return (
                              <>
                                <Typography 
                                  variant={isMobile ? "body1" : "h6"}
                                  sx={{ 
                                    fontWeight: 'bold',
                                    color: selected ? '#FFFFFF' : 'secondary.main',
                                    textAlign: 'left',
                                    fontSize: isMobile ? '0.9rem' : undefined,
                                    mb: description ? 1 : 0
                                  }}
                                >
                                  {title}
                                </Typography>
                                {description && (
                                  <Typography 
                                    variant="body1"
                                    sx={{ 
                                      color: selected ? '#FFFFFF' : 'text.secondary',
                                      textAlign: 'left',
                                      fontWeight: 'normal'
                                    }}
                                  >
                                    {description}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                        {isMobile ? (
                          <>
                            <IconButton
                              size="small"
                              sx={{
                                width: 44,
                                height: 44,
                                color: selected ? '#FFFFFF' : 'secondary.main',
                                '&:hover': {
                                  backgroundColor: selected 
                                    ? 'rgba(255, 255, 255, 0.1)' 
                                    : 'rgba(233, 30, 99, 0.1)'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMobileTooltipOpen(o.value);
                              }}
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                            
                            {/* Mobile Dialog */}
                            <Dialog
                              open={mobileTooltipOpen === o.value}
                              onClose={handleMobileTooltipClose}
                              maxWidth="sm"
                              fullWidth
                            >
                              <DialogTitle>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Box>
                                    {(() => {
                                      const colonIndex = o.label.indexOf(':');
                                      const title = colonIndex !== -1 ? o.label.substring(0, colonIndex + 1) : o.label;
                                      const description = colonIndex !== -1 ? o.label.substring(colonIndex + 1).trim() : '';
                                      
                                      return (
                                        <>
                                          <Typography variant="h6" fontWeight="bold">
                                            {title}
                                          </Typography>
                                          {description && (
                                            <Typography 
                                              variant="body1"
                                              sx={{ 
                                                fontWeight: 'normal',
                                                mt: 0.5
                                              }}
                                            >
                                              {description}
                                            </Typography>
                                          )}
                                        </>
                                      );
                                    })()}
            </Box>
                                  <IconButton
                                    onClick={handleMobileTooltipClose}
                                    size="small"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <Close />
                                  </IconButton>
          </Box>
                              </DialogTitle>
                              <DialogContent>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    whiteSpace: 'pre-line',
                                    lineHeight: 1.6
                                  }}
                                >
                                  {o.helperText}
                                </Typography>
                              </DialogContent>
                            </Dialog>
                          </>
                        ) : (
                          o.helperText && (
                            <Tooltip 
                              title={
                                <Box sx={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
                                  {o.helperText}
                                </Box>
                              }
                              placement="top"
                              arrow
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  width: 44,
                                  height: 44,
                                  color: selected ? '#FFFFFF' : 'secondary.main',
                                  '&:hover': {
                                    backgroundColor: selected 
                                      ? 'rgba(255, 255, 255, 0.1)' 
                                      : 'rgba(233, 30, 99, 0.1)'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      )}

      {/* 4. Rank skills (draggable) */}
      {step === 4 && (
        <Container maxWidth="lg">
          <SectionTitle title="Drag and drop the following designer skill sets in order of importance to you." />
          <SkillRanker
            items={SKILL_SETS}
            value={skills}
            onChange={(next) => {
              hasUserInteractedRef.current = true;
              setSkills(next);
            }}
          />
        </Container>
      )}

      {/* 5. Furniture acquisition (3 columns) + info box */}
      {step === 5 && (
        <Container maxWidth="lg">
          <SectionTitle
            title="Furniture selection & buying preference."
          />
          <Grid container spacing={2}>
            {ACQUISITION_PREFS.map((o) => {
              const selected = acquisition === o.value;
              return (
                <Grid item xs={12} sm={6} md={4} key={o.value}>
                  <Card
                  onClick={() => {
                    hasUserInteractedRef.current = true;
                    setAcquisition(o.value);
                  }}
                  sx={{
                      cursor: 'pointer',
                      height: '100%',
                      minHeight: isMobile ? '44px' : '180px',
                      border: selected 
                        ? '2px solid' 
                        : '1px solid',
                      borderColor: selected 
                        ? 'secondary.main' 
                        : 'divider',
                      backgroundColor: selected
                        ? 'secondary.main'
                        : 'transparent',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: 'secondary.main',
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <CardContent sx={{ 
                      p: isMobile ? 2 : 3, 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'flex-start' 
                    }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={isMobile ? 1 : 1.5}>
                        <Typography 
                          variant={isMobile ? "body1" : "h6"}
                          sx={{ 
                            fontWeight: 'bold',
                            color: selected ? '#FFFFFF' : 'secondary.main',
                            textAlign: 'left',
                            flex: 1,
                            fontSize: isMobile ? '0.9rem' : undefined
                          }}
                        >
                          {o.label}
                        </Typography>
                        {isMobile ? (
                          <>
                            <IconButton
                              size="small"
                              sx={{
                                width: 44,
                                height: 44,
                                color: selected ? '#FFFFFF' : 'secondary.main',
                                '&:hover': {
                                  backgroundColor: selected 
                                    ? 'rgba(255, 255, 255, 0.1)' 
                                    : 'rgba(233, 30, 99, 0.1)'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMobileTooltipOpen(o.value);
                              }}
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                            
                            {/* Mobile Dialog */}
                            <Dialog
                              open={mobileTooltipOpen === o.value}
                              onClose={handleMobileTooltipClose}
                              maxWidth="sm"
                              fullWidth
                            >
                              <DialogTitle>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="h6" fontWeight="bold">
                                    {o.label}
                                  </Typography>
                                  <IconButton
                                    onClick={handleMobileTooltipClose}
                                    size="small"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <Close />
                                  </IconButton>
            </Box>
                              </DialogTitle>
                              <DialogContent>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    whiteSpace: 'pre-line',
                                    lineHeight: 1.6
                                  }}
                                >
                                  {o.helperText}
                                </Typography>
                              </DialogContent>
                            </Dialog>
                          </>
                        ) : (
                          o.helperText && (
                            <Tooltip 
                              title={
                                <Box sx={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
                                  {o.helperText}
          </Box>
                              }
                              placement="top"
                              arrow
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  width: 44,
                                  height: 44,
                                  color: selected ? '#FFFFFF' : 'secondary.main',
                                  '&:hover': {
                                    backgroundColor: selected 
                                      ? 'rgba(255, 255, 255, 0.1)' 
                                      : 'rgba(233, 30, 99, 0.1)'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Alert
            sx={{
              mt: 4,
              borderRadius: 4,
              backgroundColor: (theme) =>
                theme.palette.mode === "light"
                  ? theme.palette.info.light
                  : theme.palette.info.dark,
            }}
            severity="info"
          >
            <Typography
              variant="h6"
              fontWeight={700}
              gutterBottom
              sx={{
                color: (theme) =>
                  theme.palette.mode === "light"
                    ? theme.palette.info.main
                    : theme.palette.info.light,
              }}
            >
              When Buying Furniture From Designer
            </Typography>
            Most design professionals make a portion of their income from furniture for your project. They purchase it
            at a wholesale price and sell it to the client at a retail price. Which is no different than the client
            buying directly from a retailer or online store. It's important to note, if you want to purchase furniture
            directly, you will be responsible for all delivery, handling, damages and or warranty issues. Some designers
            will allow homeonwers to purchase their own, while others do not as it's part of their earnings plan. It is
            not an acceptable practice to ask designers to bill you for furniture at their cost.
          </Alert>
        </Container>
      )}

      {/* 6. Gender (single-select chips) */}
      {step === 6 && (
        <Container maxWidth="lg">
          <SectionTitle title="I would prefer to work with a:" />
          <SelectOneChips
            options={GENDER_OPTIONS}
            value={genderPref}
            onChange={(value) => {
              hasUserInteractedRef.current = true;
              setGenderPref(value);
            }}
            center
          />
        </Container>
      )}

      {/* 7. Pets (multi-select chips) */}
      {step === 7 && (
        <Container maxWidth="lg">
          <SectionTitle title="I would prefer to work with an animal lover, a designer who owns :" />
          <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
            {PET_OPTIONS.map((o) => (
              <Chip
                key={o.value}
                label={o.label}
                onClick={() => toggle(petPref, setPetPref, o.value)}
                variant={petPref.includes(o.value) ? "filled" : "outlined"}
                color={petPref.includes(o.value) ? "secondary" : "default"}
                sx={{ 
                  fontSize: "1rem", 
                  py: 0.5, // 8px
                  px: 0.5, // 8px
                  height: "auto", 
                  minWidth: 'auto',
                  width: 'fit-content',
                  borderRadius: 50 
                }}
              />
            ))}
          </Box>
        </Container>
      )}

      {/* 8. LGBTQ friendly (single-select chips) */}
      {step === 8 && (
        <Container maxWidth="lg">
          <SectionTitle title="I prefer to work with an LGBTQ friendly designer." />
          <SelectOneChips
            options={YES_NO_NOPREF}
            value={lgbtq}
            onChange={(value) => {
              hasUserInteractedRef.current = true;
              setLgbtq(value);
            }}
            center
          />
        </Container>
      )}

      {/* 9. Women-owned (single-select chips) */}
      {step === 9 && (
        <Container maxWidth="lg">
          <SectionTitle title="I prefer to work with a women-owned design firm." />
          <SelectOneChips
            options={YES_NO_NOPREF}
            value={womenOwned}
            onChange={(value) => {
              hasUserInteractedRef.current = true;
              setWomenOwned(value);
            }}
            center
          />
        </Container>
      )}

      {/* 10. Minority-owned (select) */}
      {step === 10 && (
        <Container maxWidth="md">
          <SectionTitle
            title="I prefer to work with a minority owned business."
            subtitle="(Select Minority Preference)"
          />
          <Box display="flex" justifyContent="center">
            <SelectOneChips
              options={MINORITY_OPTIONS}
              value={minorityPref}
              onChange={(value) => {
                hasUserInteractedRef.current = true;
                setMinorityPref(value);
              }}
              center
            />
          </Box>
        </Container>
      )}

      {/* 11. Political alignment */}
      {step === 11 && (
        <Container maxWidth="lg">
          <SectionTitle title="I prefer to work with a designer who has the same political views as I do." />

          <Box display="flex" justifyContent="center" mb={4}>
            <SelectOneChips
              options={YES_NO_NOPREF}
              value={samePolitics}                 // starts as ""
              onChange={handleSamePoliticsChange}
              center
            />
          </Box>

          {samePolitics === "YES" && (
            <Box textAlign="center">
              <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary" }}>
                Select political affiliation
              </Typography>

              <Box display="flex" justifyContent="center">
                <SelectOneChips
                  options={POLITICAL_AFFILIATIONS}
                  value={political}               // starts as ""
                  onChange={(value) => {
                    hasUserInteractedRef.current = true;
                    setPolitical(value);
                  }}
                  center
                />
              </Box>

              {political === "OTHER" && (
                <Box mt={3} maxWidth={520} mx="auto">
                  <TextField
                    fullWidth
                    value={politicalOther}
                    onChange={(e) => {
                      hasUserInteractedRef.current = true;
                      setPoliticalOther(e.target.value);
                    }}
                    placeholder="Other"
                  />
                </Box>
              )}
            </Box>
          )}
        </Container>
      )}

      {/* 12. Same age range + desired age range (select when YES) */}
      {step === 12 && (
        <Container maxWidth="md">
          <SectionTitle title="I prefer to work with someone that is in my same age range." />
          <Box display="flex" justifyContent="center" mb={4}>
            <SelectOneChips
              options={YES_NO_NOPREF}
              value={sameAgeRange}
              onChange={handleSameAgeChange}
              center
            />
          </Box>

          {sameAgeRange === "YES" && (
            <>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, color: "text.secondary" }}
                textAlign="center"
              >
                Select desired age range
              </Typography>
              <Box display="flex" justifyContent="center">
                <SelectOneChips
                  options={AGE_RANGE_OPTIONS}
                  value={desiredAgeRange}
                  onChange={(value) => {
                    hasUserInteractedRef.current = true;
                    setDesiredAgeRange(value);
                  }}
                  center
                />
              </Box>
            </>
          )}
        </Container>
      )}


      {/* 13. Has children (single-select chips) */}
      {step === 13 && (
        <Container maxWidth="lg">
          <SectionTitle title="I prefer to work with someone who has children." />
          <Box display="flex" justifyContent="center">
            <SelectOneChips
              options={YES_NO_NOPREF}
              value={hasChildren}
              onChange={(value) => {
                hasUserInteractedRef.current = true;
                setHasChildren(value);
              }}
            />
          </Box>
        </Container>
      )}

      {/* 14. Experience (single-select chips) */}
      {step === 14 && (
        <Container maxWidth="lg">
          <SectionTitle
            title="Designer years of experience preference."
            subtitle="Note: All of our designers have a minimum of 3 years of experience."
          />
          <Box display="flex" justifyContent="center">
            <SelectOneChips
              options={EXPERIENCE_OPTIONS}
              value={experience}
              onChange={(value) => {
                hasUserInteractedRef.current = true;
                setExperience(value);
              }}
            />
          </Box>
        </Container>
      )}

      {/* 15. Lead readiness */}
      {step === 15 && (
        <Container maxWidth="lg">
          <SectionTitle title="Tell us why you are looking to hire an interior designer for your project?" />
          <Grid container spacing={2}>
            {LEAD_READINESS_OPTIONS.map((option) => (
              <Grid item xs={12} sm={6} md={4} key={option.value}>
                <SelectableCard
                  selected={hireReason === option.value}
                  onClick={() => {
                    hasUserInteractedRef.current = true;
                    setHireReason(option.value);
                    if (option.value !== "OTHER") {
                      setHireReasonOther("");
                    }
                  }}
                >
                  {option.label}
                </SelectableCard>
              </Grid>
            ))}
          </Grid>
          {hireReason === "OTHER" && (
            <Box mt={3} display="flex" justifyContent="center">
              <TextField
                value={hireReasonOther}
                onChange={(event) => {
                  hasUserInteractedRef.current = true;
                  setHireReasonOther(event.target.value);
                }}
                placeholder="Other: Write in"
                fullWidth
                sx={{ maxWidth: 520 }}
              />
            </Box>
          )}
        </Container>
      )}
    </Box>
  );
};

export default HomeownerPreferencesQuiz;
